import { FC } from 'react';

import { Tooltip } from '@dbt-labs/sourdough';

import { ErdOverlayType, OverlayState } from '../types';

interface OverlayConfig {
  key: ErdOverlayType;
  label: string;
  colorClass: string;
  dotStyle: string;
  description: string;
  disabled?: boolean;
  disabledTooltip?: string;
}

const OVERLAY_CONFIGS: OverlayConfig[] = [
  {
    key: 'relationshipTests',
    label: 'Relationship Tests',
    colorClass: 'bg-fgBrand',
    dotStyle: 'bg-fgBrand',
    description: 'Edges from relationships test definitions',
  },
  {
    key: 'semanticLayer',
    label: 'Semantic Layer',
    colorClass: 'bg-fgVizEmerald',
    dotStyle: 'bg-fgVizEmerald',
    description: 'Edges from Semantic Layer entity joins',
  },
  {
    key: 'heuristicInferred',
    label: 'Inferred',
    colorClass: 'bg-fgVizAmber',
    dotStyle: 'bg-fgVizAmber',
    description: 'Candidate relationships from column name patterns',
  },
  {
    key: 'fusionSql',
    label: 'Fusion SQL',
    colorClass: 'bg-fgDisabled',
    dotStyle: 'bg-fgDisabled',
    description: 'Available for Fusion projects',
    disabled: true,
    disabledTooltip: 'Available for Fusion projects — coming soon.',
  },
];

interface ErdOverlayControlsProps {
  overlays: OverlayState;
  onToggle: (overlay: ErdOverlayType) => void;
  pendingInferredCount: number;
  onApproveAllHighConfidence?: () => void;
}

export const ErdOverlayControls: FC<ErdOverlayControlsProps> = ({
  overlays,
  onToggle,
  pendingInferredCount,
  onApproveAllHighConfidence,
}) => {
  return (
    <div
      className="flex flex-col gap-2 rounded-lg border border-borderMuted bg-bgMain p-3 shadow-sm"
      data-testid="erd-overlay-controls"
    >
      <div className="text-xs font-semibold uppercase tracking-wider text-fgDecorative">
        Overlays
      </div>
      {OVERLAY_CONFIGS.map((config) => {
        const isActive = overlays[config.key];
        const button = (
          <button
            key={config.key}
            className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors ${
              config.disabled
                ? 'cursor-not-allowed opacity-50'
                : isActive
                  ? 'bg-bgNeutralMuted text-fgMain'
                  : 'text-fgDecorative hover:bg-bgNeutralMuted'
            }`}
            onClick={() => onToggle(config.key)}
            disabled={config.disabled}
            data-testid={`erd-overlay-toggle-${config.key}`}
          >
            <span
              className={`inline-block h-3 w-3 shrink-0 rounded-full ${config.dotStyle} ${
                !isActive && !config.disabled ? 'opacity-30' : ''
              }`}
            />
            <span className="flex-1">{config.label}</span>
            {config.key === 'heuristicInferred' && pendingInferredCount > 0 && (
              <span className="rounded-full bg-bgWarningMuted px-1.5 text-[10px] font-semibold text-fgWarning">
                {pendingInferredCount}
              </span>
            )}
            <span
              className={`text-[10px] ${isActive ? 'text-fgSuccess' : 'text-fgDecorative'}`}
            >
              {config.disabled ? 'N/A' : isActive ? 'ON' : 'OFF'}
            </span>
          </button>
        );

        if (config.disabled && config.disabledTooltip) {
          return (
            <Tooltip key={config.key} content={config.disabledTooltip}>
              {button}
            </Tooltip>
          );
        }

        return button;
      })}

      {overlays.heuristicInferred &&
        pendingInferredCount > 0 &&
        onApproveAllHighConfidence && (
          <button
            className="mt-1 rounded-md bg-bgWarningMuted px-2 py-1.5 text-xs font-medium text-fgWarning transition-colors hover:opacity-80"
            onClick={onApproveAllHighConfidence}
            data-testid="erd-approve-all-high-confidence"
          >
            Approve all high-confidence ({pendingInferredCount})
          </button>
        )}
    </div>
  );
};
